import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Constant} from '../../../global/util/Constant';
import {Router} from '@angular/router';
import {AuthService} from '../../service/auth/auth.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ApplicationService} from '../../service/application.service';
import {Dialog} from '../../../global/util/Dialog';
import {InformType} from '../../../global/util/enum/InformType';
import HttpStatusCode from '../../../global/util/HttpStatusCode';
import {LocalStorage} from '../../../global/util/LocalStorage';
import {Identificationdocument} from '../../model/Identificationdocument';
import {Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {IdentificationDocument} from '../../../global/util/IdentificationDocument';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-temp-login',
  templateUrl: './temp-login.component.html',
  styleUrls: ['./temp-login.component.css']
})
export class TempLoginComponent implements OnInit, OnDestroy {
  public userForm: FormGroup;
  public loading: boolean;
  public miniLoading: boolean;
  public fieldTextType: boolean;
  private uuid: string;
  public identificationdocuments: Identificationdocument[];
  public requestedVerCode = false;
  private observable: Subscription;
  public showEmailField = false;
  public successQuery = false;

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private authenticationService: AuthService,
              private modalService: NgbModal,
              private applicationService: ApplicationService,
              private translate: TranslateService) {
    Dialog.createInstance(modalService);
    this.createForm();
    this.getUUID();
    this.loadInitialData();
    LocalStorage.setItem('onLoginTempPage', true);
  }

  ngOnInit() {
    this.setupInitialData();
    this.observable = this.userForm.controls.documentNumber.valueChanges
      .pipe(debounceTime(500))
      .subscribe(data => {
          this.algo(this.userForm.controls.identificationdocument.value, data);
        }
      );
  }

  ngOnDestroy(): void {
    LocalStorage.removeItem('onLoginTempPage');
    this.observable.unsubscribe();
  }

  private createForm() {
    this.userForm = this.formBuilder.group({
      username: ['', Validators.required],
      identificationdocument: ['', Validators.required],
      documentNumber: ['', this.validatorBy(IdentificationDocument.cDNI)],
      email: ['', Validators.email],
      password: ['', Validators.compose([Validators.required, Validators.pattern('^[(0-9)]{6}$')])]
    });
  }

  private algo(identifi: string, documentNumber: string): void {
    this.miniLoading = true;
    const url = Constant.ROOT_API_V1 + '/public/check-existence-multiple-email';

    const formData = new FormData();
    formData.append('identificationdocument', identifi);
    formData.append('documentNumber', documentNumber);

    this.applicationService.post(url, formData).subscribe(value => {
      this.miniLoading = false;
      const hasMutipleEmails = !!value;
      this.showEmailField = hasMutipleEmails;
      if (hasMutipleEmails) {
        this.userForm.controls.email.setValidators(Validators.compose([Validators.required, Validators.email]));
      } else {
        this.userForm.controls.email.clearValidators();
      }
      this.userForm.controls.email.updateValueAndValidity();
      this.successQuery = true;
    }, error => {
      this.successQuery = false;
      this.miniLoading = false;
      this.userForm.controls.email.clearValidators();
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.VERIFICATION-CODE'), this.translate.instant('MESSAGE.ERROR-OCCURRED-TO-SEND-VERIFICATION-CODE'));
    });
  }

  private getUUID(): void {
    const url = Constant.ROOT_API_V1 + '/public/uuid';
    this.applicationService.get(url).subscribe((value: any) => {
      this.uuid = value.uuid;
    }, error => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.OBTAINING-UUID'));
    });
  }

  private loadInitialData(): void {
    const url = Constant.ROOT_API_V1 + '/public/identificationdocuments';
    this.applicationService.get(url).subscribe((value: any) => {
      this.identificationdocuments = value._embedded.identificationdocuments;
    }, error => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.OBTAINING-IDENTIFICATION-DOCUMENTS'));
    });
  }

  setupInitialData(): void {
    this.userForm.controls.identificationdocument.setValue('DNI');
  }

  login() {
    this.setValuesBefore2Save();
    if (this.userForm.invalid) {
      Object.values(this.userForm.controls).forEach(control => {
        control.markAsDirty();
      });
      return;
    }
    if (!this.requestedVerCode) {
      const control = this.userForm.get(Field.documentNumber);
      if (control.invalid) {
        control.markAsTouched();
        return;
      }
      return;
    }
    this.loading = true;
    const user = this.userForm.value;
    delete user['identificationdocument'];
    delete user['documentNumber'];
    delete user['email'];
    this.authenticationService.login(user).subscribe(data => {
      this.loading = false;
      const userType = data.headers.headers.get('usertype')[Constant.ZERO_INDEX];
      const userDocumentNumber = data.headers.headers.get('userdocumentnumber')[Constant.ZERO_INDEX];
      LocalStorage.setItem('userType', userType);
      LocalStorage.setItem('userDocumentNumber', userDocumentNumber);
      this.router.navigate(['invited-document']);
      this.applicationService.setUserLoggedIn(true);
    }, error => {
      this.loading = false;
      this.applicationService.setUserLoggedIn(false);
      if (error.status === HttpStatusCode.UNAUTHORIZED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.ACCESS-PERMISSIONS-PROBLEMS'));
      } else if (error.status === HttpStatusCode.FORBIDDEN) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.CREDENTIAL-ARE-INCORRECT'));
      } else {
        Dialog.show(InformType.Danger, this.translate.instant('TITLE.LOGIN'));
      }
    });
  }

  private setValuesBefore2Save(): void {
    const identificationDocument = this.userForm.controls.identificationdocument.value;
    const documentNumber = this.userForm.controls.documentNumber.value;
    if (!this.showEmailField) {
      this.userForm.controls.username.setValue(identificationDocument.toLowerCase() + '_' + documentNumber);
    } else {
      const email = this.userForm.controls.email.value;
      this.userForm.controls.username.setValue(identificationDocument.toLowerCase() + '_' + documentNumber + '#' + email);
    }
  }

  public getErrorMessageBy(ctrl: string): string {
    const control = this.userForm.get(ctrl);
    const hasRequiredError = control.hasError('required');
    const hasPatternError = control.hasError('pattern');
    switch (ctrl) {
      case Field.documentNumber:
        if (hasRequiredError) {
          return this.translate.instant('MESSAGE.DOCUMENT-NUMBER-REQUIRED');
        } else if (hasPatternError) {
          const actualValue = control.errors.pattern.actualValue;
          return '\''.concat(actualValue, this.translate.instant('MESSAGE.NOT-COMPLY-WITH-REQUIRED-FORMAT'));
        }
        break;
      case Field.password:
        if (hasRequiredError) {
          return this.translate.instant('MESSAGE.VERIFICATION-CODE-IS-REQUIRED');
        } else if (hasPatternError) {
          const actualValue = control.errors.pattern.actualValue;
          return '\''.concat(actualValue, this.translate.instant('MESSAGE.NOT-COMPLY-WITH-REQUIRED-FORMAT'));
        }
        break;
      case Field.email:
        const hasEmailError = control.hasError('email');
        if (hasRequiredError) {
          return this.translate.instant('MESSAGE.EMAIL-IS-REQUIRED');
        } else if (hasEmailError) {
          return '\''.concat(control.value, this.translate.instant('MESSAGE.IS-NOT-VALID-EMAIL'));
        }
        break;
    }
  }

  public isInvalidControl(ctrlName: string) {
    return this.userForm.get(ctrlName).invalid && this.userForm.get(ctrlName).dirty;
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  public toggleFieldTextType(): void {
    this.fieldTextType = !this.fieldTextType;
  }

  /**
   * Solicita el código de verificación para la autenticación
   */
  public requestVerificationCode(): void {
    const doctypeControl = this.userForm.controls.identificationdocument;
    const docNumberControl = this.userForm.controls.documentNumber;
    const emailControl = this.userForm.controls.email;
    if (docNumberControl.invalid || emailControl.invalid) {
      docNumberControl.markAsDirty();
      emailControl.markAsDirty();
      return;
    }
    const url = Constant.ROOT_API_V1 + '/public/request-verification-code';
    const formData = new FormData();
    formData.append('uuid', this.uuid);
    let value;
    const documenttypeCode = doctypeControl.value.toLowerCase();
    const documentNumber = docNumberControl.value;
    if (!this.showEmailField) {
      value = documenttypeCode + '_' + documentNumber;
    } else {
      value = documenttypeCode + '_' + documentNumber + '#' + emailControl.value;
    }
    formData.append('value', value);
    this.loading = true;
    this.applicationService.post(url, formData).subscribe((value: any) => {
      this.loading = false;
      this.requestedVerCode = true;
      const control = this.userForm.controls.password;
      control.setValidators(Validators.compose([Validators.required, Validators.pattern('^[(0-9)]{6}$')]));
      control.updateValueAndValidity();
      if (this.showEmailField) {
        Dialog.show(InformType.Success, this.translate.instant('TITLE.VERIFICATION-CODE'), this.translate.instant('MESSAGE.VERIFICATION-CODE-WAS-SUCCESSFULLY-SENT-TO-EMAIL-ENTERED'));
      } else {
        Dialog.show(InformType.Success, this.translate.instant('TITLE.VERIFICATION-CODE'), this.translate.instant('MESSAGE.VERIFICATION-CODE-WAS-SUCCESSFULLY-SENT-TO') + value._embedded.recipient);
      }
    }, error => {
      this.loading = false;
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.VERIFICATION-CODE'), this.translate.instant('MESSAGE.ERROR-OCCURRED-TO-SEND-VERIFICATION-CODE'));
    });
  }

  /**
   * Verifica que los datos necesarios para la solicitud del código de verificación sean válidos
   */
  get validRequiredInfo(): boolean {
    return this.userForm.controls.identificationdocument.valid &&
      this.userForm.controls.documentNumber.valid &&
      this.userForm.controls.email.valid;
  }

  changeDocumentValidator() {
    const code = this.userForm.controls.identificationdocument.value;
    const control = this.userForm.controls.documentNumber;
    switch (code) {
      case IdentificationDocument.cDNI:
        control.setValidators(this.validatorBy(code));
        break;
      case IdentificationDocument.cCE:
        control.setValidators(this.validatorBy(code));
        break;
      case IdentificationDocument.cPAS:
        control.setValidators(this.validatorBy(code));
        break;
    }
    control.updateValueAndValidity();
  }

  /**
   * Obtiene el validador dependiendo del código del documento de quien inicie sesión
   * @param documentCode
   * @private
   */
  private validatorBy(documentCode: string): ValidatorFn {
    switch (documentCode) {
      case IdentificationDocument.cDNI:
        return Validators.compose([Validators.required, Validators.pattern('^[(0-9)]{8}$')]);
      case IdentificationDocument.cCE:
        return Validators.compose([Validators.required, Validators.pattern('^[(0-9)]{9,12}$')]);
      case IdentificationDocument.cPAS:
        return Validators.compose([Validators.required, Validators.pattern('^[(0-9)]{9,12}$')]);
    }
  }
}

class Field {
  public static readonly documentNumber = 'documentNumber';
  public static readonly password = 'password';
  public static readonly email = 'email';
}
